/**
 * Google Analytics 4 Integration for Slimmy
 * Tracks user interactions, conversions, and engagement
 * Uses native gtag.js loaded in index.html
 */

let initialized = false;

// Helper to safely call gtag
const gtag = (...args) => {
  if (typeof window.gtag === 'function') {
    window.gtag(...args);
  }
};

/**
 * Initialize Google Analytics (called after gtag.js is loaded in index.html)
 */
export const initAnalytics = async () => {
  // Check if gtag is available (loaded by index.html)
  if (typeof window.gtag === 'function') {
    initialized = true;
    console.log('📊 Google Analytics ready');
  } else {
    // Wait a bit for gtag to load
    await new Promise(resolve => setTimeout(resolve, 1500));
    if (typeof window.gtag === 'function') {
      initialized = true;
      console.log('📊 Google Analytics ready (delayed)');
    } else {
      console.log('📊 Google Analytics not configured');
    }
  }
};

/**
 * Track page views
 */
export const trackPageView = (path, title) => {
  if (!initialized) return;
  
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title
  });
};

/**
 * Track custom events
 */
export const trackEvent = (eventName, params = {}) => {
  if (!initialized) return;
  gtag('event', eventName, params);
};

// ═══════════════════════════════════════════════════════════════
// AUTHENTICATION EVENTS
// ═══════════════════════════════════════════════════════════════

export const trackSignUp = (method = 'email') => {
  gtag('event', 'sign_up', { method });
};

export const trackLogin = (method = 'email') => {
  gtag('event', 'login', { method });
};

export const trackLogout = () => {
  gtag('event', 'logout');
};

// ═══════════════════════════════════════════════════════════════
// WEIGHT TRACKING EVENTS
// ═══════════════════════════════════════════════════════════════

export const trackWeightEntry = (weight, unit = 'kg') => {
  gtag('event', 'weight_logged', {
    weight: weight,
    unit: unit,
    value: Math.round(weight)
  });
};

export const trackGoalSet = (currentWeight, goalWeight, unit = 'kg') => {
  const diff = Math.abs(currentWeight - goalWeight);
  gtag('event', 'goal_set', {
    current_weight: currentWeight,
    goal_weight: goalWeight,
    difference: diff,
    unit: unit
  });
};

export const trackGoalReached = () => {
  gtag('event', 'goal_reached');
};

// ═══════════════════════════════════════════════════════════════
// AI CHEF EVENTS
// ═══════════════════════════════════════════════════════════════

export const trackRecipeGenerated = (mealType, diet = null) => {
  gtag('event', 'recipe_generated', {
    meal_type: mealType,
    diet: diet
  });
};

export const trackRecipeSaved = (recipeId, recipeName) => {
  gtag('event', 'recipe_saved', {
    recipe_id: recipeId,
    recipe_name: recipeName
  });
};

export const trackRecipeShared = (recipeId) => {
  gtag('event', 'share', {
    content_type: 'recipe',
    item_id: recipeId
  });
};

// ═══════════════════════════════════════════════════════════════
// CHALLENGE EVENTS
// ═══════════════════════════════════════════════════════════════

export const trackChallengeCreated = (challengeType, isPublic) => {
  gtag('event', 'challenge_created', {
    challenge_type: challengeType,
    is_public: isPublic
  });
};

export const trackChallengeJoined = (challengeId, challengeType) => {
  gtag('event', 'join_group', {
    group_id: challengeId,
    challenge_type: challengeType
  });
};

export const trackChallengeLeft = (challengeId) => {
  gtag('event', 'challenge_left', {
    challenge_id: challengeId
  });
};

export const trackChallengeCompleted = (challengeId, success) => {
  gtag('event', 'challenge_completed', {
    challenge_id: challengeId,
    success: success
  });
};

// ═══════════════════════════════════════════════════════════════
// WALK/GPS TRACKER EVENTS
// ═══════════════════════════════════════════════════════════════

export const trackWalkStarted = () => {
  gtag('event', 'walk_started');
};

export const trackWalkCompleted = (distanceKm, durationMinutes, calories) => {
  gtag('event', 'walk_completed', {
    distance_km: distanceKm,
    duration_minutes: durationMinutes,
    calories: calories,
    value: Math.round(distanceKm * 10) / 10
  });
};

export const trackWalkPaused = () => {
  gtag('event', 'walk_paused');
};

export const trackWalkResumed = () => {
  gtag('event', 'walk_resumed');
};

// ═══════════════════════════════════════════════════════════════
// SOCIAL/FEED EVENTS
// ═══════════════════════════════════════════════════════════════

export const trackPostCreated = (postType) => {
  gtag('event', 'post_created', {
    post_type: postType
  });
};

export const trackPostLiked = (postId) => {
  gtag('event', 'post_liked', {
    post_id: postId
  });
};

export const trackPostCommented = (postId) => {
  gtag('event', 'post_commented', {
    post_id: postId
  });
};

export const trackPostShared = (postId) => {
  gtag('event', 'share', {
    content_type: 'post',
    item_id: postId
  });
};

// ═══════════════════════════════════════════════════════════════
// PREMIUM/MONETIZATION EVENTS
// ═══════════════════════════════════════════════════════════════

export const trackPremiumPageViewed = () => {
  gtag('event', 'view_item', {
    items: [{
      item_name: 'Premium Subscription',
      item_category: 'subscription'
    }]
  });
};

export const trackPremiumCheckoutStarted = (plan, price, currency = 'EUR') => {
  gtag('event', 'begin_checkout', {
    currency: currency,
    value: price,
    items: [{
      item_name: `Premium ${plan}`,
      price: price,
      quantity: 1
    }]
  });
};

export const trackPremiumPurchased = (plan, price, currency = 'EUR') => {
  gtag('event', 'purchase', {
    transaction_id: `premium_${Date.now()}`,
    currency: currency,
    value: price,
    items: [{
      item_name: `Premium ${plan}`,
      price: price,
      quantity: 1
    }]
  });
};

export const trackTrialStarted = () => {
  gtag('event', 'trial_started');
};

export const trackAdViewed = (adPosition) => {
  gtag('event', 'ad_impression', {
    ad_position: adPosition
  });
};

export const trackAdClicked = (adPosition, bannerId) => {
  gtag('event', 'ad_click', {
    ad_position: adPosition,
    banner_id: bannerId
  });
};

// ═══════════════════════════════════════════════════════════════
// AI BODY TRANSFORMATION EVENTS
// ═══════════════════════════════════════════════════════════════

export const trackTransformationStarted = () => {
  gtag('event', 'transformation_started');
};

export const trackTransformationCompleted = () => {
  gtag('event', 'transformation_completed');
};

export const trackTransformationShared = () => {
  gtag('event', 'share', {
    content_type: 'transformation'
  });
};

// ═══════════════════════════════════════════════════════════════
// ENGAGEMENT EVENTS
// ═══════════════════════════════════════════════════════════════

export const trackNotificationEnabled = () => {
  gtag('event', 'notifications_enabled');
};

export const trackNotificationDismissed = () => {
  gtag('event', 'notifications_dismissed');
};

export const trackAppInstalled = () => {
  gtag('event', 'app_install');
};

export const trackLanguageChanged = (language) => {
  gtag('event', 'language_changed', {
    language: language
  });
};

export const trackThemeChanged = (theme) => {
  gtag('event', 'theme_changed', {
    theme: theme
  });
};

// ═══════════════════════════════════════════════════════════════
// REFERRAL EVENTS
// ═══════════════════════════════════════════════════════════════

export const trackReferralCodeShared = () => {
  gtag('event', 'referral_code_shared');
};

export const trackReferralCodeUsed = (referralCode) => {
  gtag('event', 'referral_code_used', {
    referral_code: referralCode
  });
};

// ═══════════════════════════════════════════════════════════════
// ERROR TRACKING
// ═══════════════════════════════════════════════════════════════

export const trackError = (errorType, errorMessage) => {
  gtag('event', 'exception', {
    description: `${errorType}: ${errorMessage}`,
    fatal: false
  });
};

// ═══════════════════════════════════════════════════════════════
// USER PROPERTIES
// ═══════════════════════════════════════════════════════════════

export const setUserProperties = (properties) => {
  if (!initialized) return;
  gtag('set', 'user_properties', properties);
};

export const setUserId = (userId) => {
  if (!initialized) return;
  gtag('set', { user_id: userId });
};

export const setUserPremiumStatus = (isPremium) => {
  setUserProperties({
    user_type: isPremium ? 'premium' : 'free'
  });
};

export default {
  initAnalytics,
  trackPageView,
  trackEvent,
  trackSignUp,
  trackLogin,
  trackLogout,
  trackWeightEntry,
  trackGoalSet,
  trackGoalReached,
  trackRecipeGenerated,
  trackRecipeSaved,
  trackRecipeShared,
  trackChallengeCreated,
  trackChallengeJoined,
  trackChallengeLeft,
  trackChallengeCompleted,
  trackWalkStarted,
  trackWalkCompleted,
  trackWalkPaused,
  trackWalkResumed,
  trackPostCreated,
  trackPostLiked,
  trackPostCommented,
  trackPostShared,
  trackPremiumPageViewed,
  trackPremiumCheckoutStarted,
  trackPremiumPurchased,
  trackTrialStarted,
  trackAdViewed,
  trackAdClicked,
  trackTransformationStarted,
  trackTransformationCompleted,
  trackTransformationShared,
  trackNotificationEnabled,
  trackNotificationDismissed,
  trackAppInstalled,
  trackLanguageChanged,
  trackThemeChanged,
  trackReferralCodeShared,
  trackReferralCodeUsed,
  trackError,
  setUserProperties,
  setUserId,
  setUserPremiumStatus
};
